import * as React from 'react';
import { useState, useEffect, useMemo } from 'react';

/** Material UI Packages. */
import {
    Box,
    Dialog,
    Typography,
    FormControl,
    FormControlLabel,
    RadioGroup,
    Radio,
    FormLabel,
    Slide,
    Paper,
    OutlinedInput,
    Select,
    MenuItem,
} from '@mui/material';
import { randomId } from '@mui/x-data-grid-generator';

/** Material UI Icons. */
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';

/** Redux Packages. */
import { useSelector, useDispatch } from 'react-redux';

/** Redux Reducers. */
import { setSnackbar } from 'shared/store/slices/snackBarSlice';

/** Auth0 authenticator Packages. */
import { useAuth0 } from '@auth0/auth0-react';

/** Internal Components. */
import { PaperComponent } from 'shared/ui/DraggableDialog';
import { CustomSkeleton2 } from 'shared/ui/Skeleton';
import ThemeButton from 'shared/ui/Button';
import DialogHeader from 'shared/ui/DialogHeader';
import { createSocketData, updateSocketData, deleteSocketData } from 'shared/lib/fetchSocket';
import { handleDialogCloseWithDetection } from 'shared/lib/changeDetection';
import { progressNoteEntries } from 'shared/config/constantsData';

import CommentBox from './CommentBox';

/** Mapping reducer names for each tab value. */
const reducerName = {
    0: ['addOimComment', 'setOimCommentUpdate', 'deleteOimComment'],
    1: ['addOexComment', 'setOexCommentUpdate', 'deleteOexComment'],
    2: ['addAimComment', 'setAimCommentUpdate', 'deleteAimComment'],
    3: ['addAexComment', 'setAexCommentUpdate', 'deleteAexComment'],
    4: ['addTruckingComment', 'setTruckingCommentUpdate', 'deleteTruckingComment'],
};

/** Mapping key names for different shipment types. */
const keyName = {
    0: ['il_ocean_import_id', 'oimCommentUpdateForm'],
    1: ['il_ocean_export_id', 'oexCommentUpdateForm'],
    2: ['il_air_import_id', 'aimCommentUpdateForm'],
    3: ['il_air_export_id', 'aexCommentUpdateForm'],
    4: ['il_ftl_ltl_id', 'truckingCommentUpdateForm'],
};

/** Max length of comment input field. */
const MAX_CHARACTER_LIMIT = 500;
/** Initial state for form field, reply mode and edit mode. */
const INITIAL_FORM_FIELD = { type: 'todo', content: '', note_entry: '' };
const INITIAL_REPLY_MODE = { active: false, replyRow: {} };
const INITIAL_EDIT_MODE = { active: false, editRow: {} };

export default function Comment(props) {
    /** Get props from Shipment.js */
    const { commentOpen, setCommentOpen, row } = props;

    /** Destructuring values from useAuth0 hook. */
    const { user } = useAuth0();

    /** Get the dispatch function from Redux to send actions to the store. */
    const dispatch = useDispatch();

    /** Shipment type data. */
    const tabValue = useSelector((state) => state.tabValueForm);

    /** Function to dynamically select a state. */
    const useDynamicSelector = (stateKey) => {
        const dynamicStateKey = `${stateKey}`;
        return useSelector((state) => state[dynamicStateKey]);
    };
    /** Stored commnet data. */
    const commentData = useDynamicSelector(keyName[tabValue][1]);

    /** State for datagrid row. */
    const [commentRows, setCommentRows] = useState([]);
    /** State for user input form field. */
    const [formField, setFormField] = useState(INITIAL_FORM_FIELD);
    /** State for managing reply mode. */
    const [replyMode, setReplyMode] = useState(INITIAL_REPLY_MODE);
    /** State for managing edit mode. */
    const [editMode, setEditMode] = useState(INITIAL_EDIT_MODE);
    /** State for data loading. */
    const [initialLoading, setInitialLoading] = useState(true);

    /** Validate whether the user can select the type selection. */
    const isDisabled = useMemo(() => {
        // If reply mode is active, disable the type selection.
        if (replyMode.active) return true;
        // If edit mode is active and the type is 'reply', disable the type selection.
        if (editMode.active && formField.type === 'reply') return true;

        return false;
    }, [replyMode.active, editMode.active, formField.type]);

    const matchedKeyname = useMemo(() => keyName[tabValue][0], [tabValue]);
    const matchedReducer = useMemo(
        () => (editMode.active ? reducerName[tabValue][1] : reducerName[tabValue][0]),
        [tabValue, editMode.active],
    );

    /** Compute note_entry values already used for this shipment row (main comments only). */
    const usedNoteEntries = useMemo(() => {
        if (!row || !commentData) return new Set();
        return new Set(
            commentData
                .filter(
                    (item) =>
                        item[matchedKeyname] === row[0].id &&
                        item.il_type === 'note' &&
                        item.sub_id === 0 &&
                        item.note_entry &&
                        // In edit mode, exclude the entry being edited so it stays enabled.
                        !(editMode.active && item.id === editMode.editRow.id),
                )
                .map((item) => item.note_entry),
        );
    }, [commentData, row, matchedKeyname, editMode.active, editMode.editRow.id]);

    /** Initialization of commentRows */
    useEffect(() => {
        if (!row || !commentData) return;

        const filteredRow = commentData.filter((item) => item[matchedKeyname] === row[0].id);
        const sortedRow = filteredRow.sort((a, b) => a.id - b.id).filter((item) => item.sub_id === 0);
        const replyComment = filteredRow.filter((item) => item.sub_id !== 0);

        const finalSortedRow = sortedRow.map((item) => {
            const subItems = replyComment.filter((val) => val.sub_id === item.id);
            return [item, ...subItems];
        });

        setInitialLoading(false);
        setCommentRows(finalSortedRow);
    }, [commentData, row, matchedKeyname]);

    /** Validate whether the text field is empty and type is selected. */
    const isValid = () => {
        if (!formField.content || formField.content.trim() === '') {
            return false;
        }
        if (formField.type !== 'todo' && formField.type !== 'note' && formField.type !== 'reply') {
            return false;
        }
        return true;
    };

    /** Hanlde to close a dialog. */
    const handleClose = () => {
        handleDialogCloseWithDetection(null, formField, INITIAL_FORM_FIELD, () => {
            setCommentOpen(false);
            handleReset();
        });
    };

    /** Handler to add, update and edit note or todo. */
    const handleAddClick = () => {
        if (!isValid()) {
            dispatch(
                setSnackbar({ open: true, message: 'Please enter a comment and select a type.', severity: 'warning' }),
            );
            return;
        }

        const new_id = randomId();
        const formattedRow = {
            id: editMode.active ? editMode.editRow.id : new_id,
            il_opus_reference_id: editMode.active ? editMode.editRow.il_opus_reference_id : '',
            [matchedKeyname]: row[0].id,
            sub_id: replyMode.active ? replyMode.replyRow.id : editMode.active ? editMode.editRow.sub_id : 0,
            il_type: formField.type,
            content: formField.content,
            note_entry: formField.type === 'note' ? formField.note_entry : '',
            todo_status: replyMode.active ? null : editMode.active ? editMode.editRow.todo_status : false,
            registered_by: user.nickname.charAt(0).toUpperCase() + user.nickname.slice(1).toLowerCase(),
            registered_by_email: user.email,
            modified_on: new Date(),
        };

        /** Send a socket emit in edit mode. */
        if (editMode.active) {
            updateSocketData(dispatch, tabValue, 'row', 'il_comment', matchedReducer, formattedRow, () => {
                dispatch(setSnackbar({ open: true, message: 'Successfully updated.', severity: 'success' }));
            });
        } else {
            createSocketData(dispatch, tabValue, 'row', 'il_comment', matchedReducer, formattedRow, () => {
                dispatch(setSnackbar({ open: true, message: 'Successfully created.', severity: 'success' }));
            });
        }
        handleReset();
    };

    /** Handler to activate reply mode. */
    const handleReplyClick = (comment) => {
        setFormField((prev) => ({ type: 'reply', content: '' }));
        setReplyMode({ active: true, replyRow: comment });
        setEditMode(INITIAL_EDIT_MODE);
    };

    /** Handler to activate edit mode */
    const handleEditClick = (comment) => {
        setFormField({ type: comment.il_type, content: comment.content, note_entry: comment.note_entry || '' });
        setReplyMode(INITIAL_REPLY_MODE);
        setEditMode({ active: true, editRow: comment });
    };

    /** Handler to delete exist row. */
    const handleDeleteClick = (row) => {
        const deletedId = row.id;
        const matchedReducer = reducerName[tabValue][2];
        if (window.confirm('Are you sure you want to delete the note?')) {
            deleteSocketData(dispatch, tabValue, 'row', 'il_comment', matchedReducer, deletedId, (receivedData) => {
                dispatch(setSnackbar({ open: true, message: 'Successfully deleted.', severity: 'success' }));
                handleReset();
            });
        }
    };

    /** Handler to change to do status. */
    const handleStatusClick = (comment) => {
        const matchedReducer = reducerName[tabValue][1];
        const formattedRow = {
            ...comment,
            todo_status: !comment.todo_status,
            registered_by: user.nickname.charAt(0).toUpperCase() + user.nickname.slice(1).toLowerCase(),
            registered_by_email: user.email,
            modified_on: new Date(),
        };
        updateSocketData(dispatch, tabValue, 'row', 'il_comment', matchedReducer, formattedRow, () => {
            dispatch(setSnackbar({ open: true, message: 'Successfully updated.', severity: 'success' }));
        });
    };

    /** Handler to change comment type. */
    const handleTypeChange = (event) => {
        setFormField((prev) => ({ type: event.target.value, content: prev.content, note_entry: '' }));
    };

    /** Handler to change note_entry selection. */
    const handleNoteEntryChange = (event) => {
        setFormField((prev) => ({ ...prev, note_entry: event.target.value }));
    };

    /** Handler to change comment content. */
    const handleContentChange = (event) => {
        setFormField((prev) => ({ ...prev, content: event.target.value }));
    };

    /** Handler to reset all state. */
    const handleReset = () => {
        setFormField(INITIAL_FORM_FIELD);
        setReplyMode(INITIAL_REPLY_MODE);
        setEditMode(INITIAL_EDIT_MODE);
    };

    /** Set of handlers for easy passing to child components. */
    const handlers = {
        handleDeleteClick,
        handleEditClick,
        handleReplyClick,
        handleStatusClick,
    };

    return (
        <Dialog
            maxWidth="sm"
            fullWidth={true}
            onClose={handleClose}
            open={commentOpen}
            PaperComponent={PaperComponent}
            PaperProps={{
                sx: {
                    borderRadius: '18px',
                },
            }}
        >
            {!initialLoading ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', maxHeight: 800, minHeight: 0 }}>
                    <DialogHeader
                        cursor
                        title={`NOTE | ${row ? row[0]?.il_opus_reference_id : ''}`}
                        onClose={handleClose}
                    />
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            minHeight: 0,
                            p: 1,
                            bgcolor: '#dfdfdf',
                        }}
                    >
                        <Box
                            sx={{
                                flex: '1 1 auto',
                                minHeight: 0,
                                width: '100%',
                                maxHeight: '26rem',
                                overflowY: 'auto',
                                pb: '10px',
                            }}
                        >
                            {commentRows.map((comments, index) => (
                                <CommentBox
                                    comments={comments}
                                    key={index}
                                    tabValue={tabValue}
                                    replyMode={replyMode}
                                    editMode={editMode}
                                    handlers={handlers}
                                />
                            ))}
                        </Box>
                        <Box sx={{ flexShrink: 0, width: '100%', p: 0, m: 0, mt: 1 }}>
                            <Slide direction="up" in={replyMode.active} mountOnEnter unmountOnExit>
                                <Paper
                                    sx={{
                                        mb: 1,
                                        px: 2,
                                        bgcolor: 'white',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                    }}
                                >
                                    <Box>
                                        <Typography sx={{ fontSize: 13, color: '#4aa8db' }}>
                                            Reply to {replyMode.replyRow.registered_by}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: 13,
                                                display: '-webkit-box',
                                                WebkitBoxOrient: 'vertical',
                                                WebkitLineClamp: 1,
                                                overflow: 'hidden',
                                            }}
                                        >
                                            "{replyMode.replyRow.content}"
                                        </Typography>
                                    </Box>
                                </Paper>
                            </Slide>
                            <Box sx={{ display: 'flex' }}>
                                <FormControl>
                                    <FormLabel id="type" sx={{ fontSize: 13, color: '#003a52', fontWeight: 500 }}>
                                        Type
                                    </FormLabel>
                                    <RadioGroup
                                        aria-labelledby="type"
                                        defaultValue="todo"
                                        name="type"
                                        value={formField.type}
                                        onChange={handleTypeChange}
                                    >
                                        <FormControlLabel
                                            value={'todo'}
                                            control={
                                                <Radio
                                                    sx={{
                                                        height: '28px',
                                                        '& .MuiSvgIcon-root': {
                                                            fontSize: 15,
                                                        },
                                                    }}
                                                />
                                            }
                                            label={
                                                <Typography
                                                    sx={{
                                                        fontSize: 12,
                                                        color: isDisabled ? '#5a7c89' : '#003a52',
                                                        fontWeight: 500,
                                                    }}
                                                >
                                                    TODO
                                                </Typography>
                                            }
                                            disabled={isDisabled}
                                        />
                                        <FormControlLabel
                                            value={'note'}
                                            control={
                                                <Radio
                                                    sx={{
                                                        height: '28px',
                                                        '& .MuiSvgIcon-root': {
                                                            fontSize: 15,
                                                        },
                                                    }}
                                                />
                                            }
                                            label={
                                                <Typography
                                                    sx={{
                                                        fontSize: 12,
                                                        color: isDisabled ? '#5a7c89' : '#003a52',
                                                        fontWeight: 500,
                                                    }}
                                                >
                                                    NOTE
                                                </Typography>
                                            }
                                            disabled={isDisabled}
                                        />
                                    </RadioGroup>
                                    <Box
                                        sx={{
                                            visibility:
                                                formField.type === 'note' && !replyMode.active ? 'visible' : 'hidden',
                                        }}
                                    >
                                        <Select
                                            size="small"
                                            displayEmpty
                                            value={formField.note_entry}
                                            onChange={handleNoteEntryChange}
                                            MenuProps={{
                                                PaperProps: {
                                                    sx: {
                                                        width: 120,
                                                        maxWidth: 120,
                                                        boxSizing: 'border-box',
                                                    },
                                                },
                                                anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                                                transformOrigin: { vertical: 'top', horizontal: 'left' },
                                            }}
                                            sx={{
                                                fontSize: '12px',
                                                width: 120,
                                                maxWidth: '100%',
                                                boxSizing: 'border-box',
                                                '& .MuiSelect-select': {
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    padding: '6px 6px',
                                                },
                                            }}
                                        >
                                            <MenuItem
                                                value=""
                                                sx={{
                                                    fontSize: '11px',
                                                    whiteSpace: 'normal',
                                                    wordBreak: 'break-word',
                                                }}
                                            >
                                                — No entry —
                                            </MenuItem>
                                            {progressNoteEntries[tabValue].map((opt) => (
                                                <MenuItem
                                                    key={opt.value}
                                                    value={opt.value}
                                                    disabled={usedNoteEntries.has(opt.value)}
                                                    sx={{
                                                        fontSize: '11px',
                                                        whiteSpace: 'normal',
                                                        wordBreak: 'break-word',
                                                    }}
                                                >
                                                    {opt.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </Box>
                                </FormControl>
                                <OutlinedInput
                                    placeholder="Please leave a comment..."
                                    label=""
                                    multiline
                                    maxRows={8}
                                    minRows={8}
                                    value={formField.content}
                                    onChange={handleContentChange}
                                    sx={{
                                        fontSize: '13px',
                                        '& .MuiInputBase-root': {
                                            fontSize: '13px',
                                            p: 1,
                                        },
                                        width: '83%',
                                        bgcolor: 'white',
                                        p: 1,
                                        ml: 1,
                                        mr: 0.5,
                                        borderRadius: 1,
                                    }}
                                    slotProps={{
                                        input: {
                                            maxLength: MAX_CHARACTER_LIMIT,
                                            startadornment: replyMode.active ? (
                                                <Box
                                                    sx={{
                                                        position: 'relative',
                                                        pb: 6,
                                                    }}
                                                >
                                                    <SubdirectoryArrowRightIcon
                                                        sx={{ fontSize: '15px', color: '#c8c8c8' }}
                                                    />
                                                </Box>
                                            ) : null,
                                        },
                                    }}
                                />
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'space-around',
                                        flexWrap: 'wrap',
                                    }}
                                >
                                    <ThemeButton
                                        text={replyMode.active ? 'REPLY' : editMode.active ? 'EDIT' : 'ADD'}
                                        width={'68px'}
                                        onClick={handleAddClick}
                                    />
                                    {replyMode.active || editMode.active ? (
                                        <ThemeButton text={'Cancel'} width={'68px'} onClick={handleReset} />
                                    ) : null}
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            ) : (
                <CustomSkeleton2 />
            )}
        </Dialog>
    );
}
